import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useUser } from "../../users/providers/UserProvider";
import { getToken } from "../../users/service/localStorage";
import { pendingActionTradeIds } from "../tradeUiLogic";
import useAppResume from "../../native/useAppResume";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Same cadence as NotificationProvider — nothing here is push-driven yet.
const POLL_INTERVAL_MS = 30_000;

/**
 * Deliberately axios directly, NOT `getMyTradeRequests` from `../tradeApi` (#119).
 *
 * Every tradeApi helper flattens its failure to a bare message STRING, because
 * `TradesPage` passes the rejected value straight into `setSnack` as the message
 * and `TradePresenceCheckIn`'s `confirmMet` branches on `typeof err === "string"`.
 * That contract is load-bearing there and must not widen — but a string has no
 * `.response`, so the 401/403 backstop below could never fire through it. The
 * poll ran every 30 s forever against an expired token, firing one red toast per
 * tick on every screen with the user touching nothing.
 *
 * Calling axios here yields the real AxiosError at the point that needs the
 * status, and keeps the raw error inside this provider's own try/catch, where
 * nobody can mistake it for a message.
 */

// Attach the JWT explicitly rather than relying on axios.defaults, which can be
// briefly unset while the Supabase→app-token bridge runs. Without this the
// first poll of a cold start is a guaranteed 401 — and now that the backstop
// actually fires, that would stop the badge on every launch.
const authConfig = () => {
  const token = getToken();
  return token ? { headers: { "x-auth-token": token } } : null;
};

// `syncedAt`, `tradesSig`, `completedCount` and `promiseCount` (see `sync`
// below) are left out of the default on purpose: undefined reads as "no poll
// yet" everywhere they are used, and every byte of the main chunk counts
// against its budget.
const TradeActionContext = createContext({
  pendingActionCount: 0,
  pendingActionIds: [],
  refresh: async () => {},
});

export const TradeActionProvider = ({ children }) => {
  const { user, token } = useUser();
  // The account this provider currently serves. A reply for any other
  // account (signed out, or switched while it was in flight) is dropped:
  // it must not write that account's data into this session.
  // (Code review SEPARATION-7)
  const liveUserIdRef = useRef(user?._id ?? null);
  liveUserIdRef.current = user?._id ?? null;
  // The ids, not just the count: the Trades badge unions them with trades
  // that have an unread update (tradesBadgeCount), so a trade is counted once.
  const [ids, setIds] = useState([]);
  // What the rest of the app reads off this poll besides the badge:
  // - syncedAt: when the server last answered. The Trades and Wishlist error
  //   screens retry on it, so they recover once the server is back (B-61).
  // - tradesSig: the poll's answer as a string, so it changes when any trade
  //   is added or touched (status, confirmations, updatedAt). The Trades list
  //   reloads itself on that instead of waiting for a tab switch (B-50).
  // - completedCount: how many of the user's trades this session has watched
  //   become completed. The collection and wishlist reload when it moves,
  //   because a completion moves cards on the server (B-56), including when
  //   the OTHER collector's tap is the one that completes it.
  // - promiseCount: how many of the user's trades this session has watched
  //   move into or out of `accepted`, the one status that holds cards
  //   (COMMITTED_STATUSES on the server). The collection reloads when it
  //   moves, so a card's "In a trade" mark appears after an accept and goes
  //   after an abandon or a report (B-14), whichever phone did it.
  const [sync, setSync] = useState({ syncedAt: 0, tradesSig: "", completedCount: 0, promiseCount: 0 });
  // Each trade's status at the last successful poll; null before the first, so
  // trades that were already completed at sign-in never count as news.
  const lastStatus = useRef(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const userId = user?._id;

  // A plain promise chain rather than async/await: the main chunk is
  // transpiled for older WebViews, where every await costs a state machine.
  const fetchCount = useCallback(() => {
    // No user, or no token yet (e.g. bridge in progress): skip quietly, no
    // request and no 401.
    const cfg = userId && authConfig();
    if (!cfg) return Promise.resolve();
    const get = (path) => axios.get(apiUrl + "/trades" + path, cfg);
    // The light read (B-51): ids, status, completion flags, updatedAt.
    return get("/summary")
      .catch((err) => {
        // A server from before /summary reads "summary" as a trade id and
        // answers 400 (or 404). The full list carries the same fields.
        const status = err?.response?.status;
        if (status === 400 || status === 404) return get("");
        throw err;
      })
      .then(({ data }) => {
        if (String(liveUserIdRef.current) !== String(userId)) return;
        const list = Array.isArray(data) ? data : [];
        const before = lastStatus.current;
        lastStatus.current = new Map(list.map((t) => [t._id, t.status]));
        setIds(pendingActionTradeIds(list, userId));
        // [was, now] for each trade whose status changed since the last poll.
        const moves = before
          ? list.map((t) => [before.get(t._id), t.status]).filter((m) => m[0] !== m[1])
          : [];
        setSync((s) => ({
          syncedAt: Date.now(),
          tradesSig: JSON.stringify(list),
          completedCount: s.completedCount + moves.filter((m) => m[1] === "completed").length,
          promiseCount: s.promiseCount + moves.filter((m) => m.includes("accepted")).length,
        }));
      })
      .catch((err) => {
        // Badge is best-effort UI sugar over trade state, not a source of truth —
        // a failed poll just leaves the last known count. Stop hammering an
        // unauthenticated backend the same way NotificationProvider does. Only
        // auth failures are hopeless: a 5xx or a dropped connection says nothing
        // about this session, so the interval keeps running through those.
        const status = err?.response?.status;
        if (status === 401 || status === 403) stopPolling();
      });
  }, [userId, stopPolling]);

  // A different account (or none) starts with no history to compare with.
  // Its own effect, ahead of the poll's, so a token refresh alone (which
  // re-runs only the poll's) does not forget the statuses it last saw.
  useEffect(() => {
    lastStatus.current = null;
  }, [userId]);

  // Keyed on the token as well as the account (same reasoning as P2-32 in
  // NotificationProvider): an ordinary expired-token 401 carries no revocation
  // code, so the interceptor does NOT sign the user out and `_id` never moves.
  // Keyed on the account alone, the stop above would be permanent for the rest
  // of the session — trading an endless flood for a dead badge. The refresh
  // that repairs the 401 mints a new token, and that is what restarts polling.
  useEffect(() => {
    if (!userId) {
      setIds([]);
      return undefined;
    }
    fetchCount();
    intervalRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [userId, token, fetchCount, stopPolling]);

  // Coming back to the app polls at once instead of up to 30 s later (B-50).
  // Not while the poll is stopped on a 401/403: the next token restarts it.
  useAppResume(() => {
    if (intervalRef.current) fetchCount();
  });

  return (
    <TradeActionContext.Provider
      value={{ pendingActionCount: ids.length, pendingActionIds: ids, ...sync, refresh: fetchCount }}
    >
      {children}
    </TradeActionContext.Provider>
  );
};

export const useTradeActions = () => useContext(TradeActionContext);
